import { useEffect, useCallback } from "react";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import type { ClienteFinal } from "@/lib/Interfaces/user.interface";

interface BrandingProviderProps {
  children: React.ReactNode;
}

// ── Defaults del sistema (RetroIA) ──────────────────────────────────────────
const DEFAULTS = {
  colorPrimario: "#7c3aed",
  colorSecundario: "#3b82f6",
  colorTextoPrimario: "#1f2937",
  colorTerciario1: "#dbe0dc",
  colorTerciario2: "#e5e7eb",
  tipografia: "Inter",
} as const;

// ── Helpers de color ─────────────────────────────────────────────────────────
function lightenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0x00ff) + amount);
  const b = Math.min(255, (num & 0x0000ff) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function darkenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0x00ff) - amount);
  const b = Math.max(0, (num & 0x0000ff) - amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// ── Función principal de aplicación de branding ──────────────────────────────
function applyBranding(
  clienteFinal: ClienteFinal | null | undefined,
  isDark: boolean,
): void {
  const root = document.documentElement;

  const p = clienteFinal?.colorPrimario || DEFAULTS.colorPrimario;
  const s = clienteFinal?.colorSecundario || DEFAULTS.colorSecundario;
  const t = clienteFinal?.colorTextoPrimario || DEFAULTS.colorTextoPrimario;
  const t1 = clienteFinal?.colorTerciario1 || DEFAULTS.colorTerciario1;
  const t2 = clienteFinal?.colorTerciario2 || DEFAULTS.colorTerciario2;
  const font = clienteFinal?.tipografiaMarca || DEFAULTS.tipografia;

  // Colores principales (ajustados por modo oscuro)
  root.style.setProperty("--primary", isDark ? lightenColor(p, 40) : p);
  root.style.setProperty("--secondary", isDark ? lightenColor(s, 30) : s);
  // En dark mode el texto siempre debe ser claro — no depende del color del cliente
  root.style.setProperty("--foreground", isDark ? "#f1f5f9" : t);

  // Bordes y estado desactivado
  root.style.setProperty("--border", isDark ? lightenColor(t2, 20) : t2);
  root.style.setProperty(
    "--color-primary-disabled",
    isDark ? darkenColor(t2, 10) : lightenColor(t2, 10),
  );
  // Tipografía
  root.style.setProperty("--brand-font-family", font);

  // Variables de compatibilidad (--brand-*) para componentes legacy
  root.style.setProperty("--brand-primary", p);
  root.style.setProperty("--brand-secondary", s);
  root.style.setProperty("--brand-tertiary", t1);
  root.style.setProperty("--brand-accent", t2);
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { usuarioInfo } = useCurrentUser();

  const applyCurrentBranding = useCallback(() => {
    const isDark = document.documentElement.classList.contains("dark");
    applyBranding(usuarioInfo?.aprendiz?.clienteFinal, isDark);
  }, [usuarioInfo]);

  // Aplicar branding cuando cambia el usuario
  useEffect(() => {
    applyCurrentBranding();
  }, [applyCurrentBranding]);

  // Re-aplicar cuando cambia el tema (dark/light)
  useEffect(() => {
    const observer = new MutationObserver(applyCurrentBranding);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [applyCurrentBranding]);

  return <>{children}</>;
}
